import { defineStore } from "pinia";

// Libraries
import axios from "axios";
import type { Endpoints } from "@octokit/types";
import { markRaw, Raw, toRaw } from "vue";

// Utilities
import { sdk } from "@/js/sdk";
import { extractSdkErrorMessage } from "@/js/sdkError";
import { SdkError, ERROR_CODES, type RepositoryPatchRemovableField } from "@signaloid/cloud-compute-engine-client";
import * as dsUtil from "@/components/DataSources/utilities";
import * as util from "@/js/utilities";
import { monitoringCaptureError } from "@/plugins/monitoring";
import { hydrateCachedList, mirrorStoreListToCache, readCachedList } from "@/js/listCache";
import { pickKeys, saveOrRevert, WriteGenerations } from "@/js/optimisticWrites";

// Types
import { Repository } from "@/types/api/repositories";
import { RepositoryConnectionRequestConfig } from "@/js/signaloidClient.types";
import { isLocalMountConfig, LocalMountConfig } from "@/types/api/dataSources";
import { RemoteAccessState } from "@/types/general";

// Stores
import { useDataSourcesStore } from "./dataSources";

type StoreState = {
	connectedRepositories: undefined | Repository[];
	remoteRepositoryUpdateStatus: RemoteAccessState;
	repositoryListServedFromCache: boolean;
	repositoryListFetchInFlight: undefined | Promise<void>;
	confirmedRepositoryIDs: Set<string>;
	// Keyed `${repositoryID}:${field}`: two repositories never contend for the same field.
	pendingFieldServerValues: Raw<Map<string, unknown>>;
	fieldWriteGenerations: Raw<WriteGenerations<string>>;
	pendingRepositoryWrites: Raw<Map<string, Set<Promise<RepositoryWriteOutcome>>>>;
	fieldWriteConfirmations: Raw<Map<string, number>>;
	disconnectConfirmations: Raw<Map<string, number>>;
	repositoryHostDetails: Record<string, RepositoryHostDetails>;
};

export type RepositoryHostDetails = Pick<
	Endpoints["GET /repos/{owner}/{repo}"]["response"]["data"],
	"html_url" | "full_name" | "description" | "topics" | "visibility" | "updated_at" | "forks_count"
> & { license: { name: string } | null };

export type RepositoryWriteOutcome = "saved" | "failed" | "superseded";

type CachedRepositoryHostDetails = RepositoryHostDetails & { RepositoryID: string };

const REPOSITORY_LIST_CACHE_KEY = "repository-list";
const REPOSITORY_HOST_DETAILS_CACHE_KEY = "repository-host-details";

let writeConfirmationCount = 0;

// Cached lists are untrusted local input: keep only entries the app can actually render.
function isCachedRepository(entry: unknown): entry is Repository {
	if (typeof entry !== "object" || entry === null) {
		return false;
	}
	const candidate = entry as Partial<Repository>;
	return typeof candidate.RepositoryID === "string" && typeof candidate.RemoteURL === "string";
}

function isCachedRepositoryHostDetails(entry: unknown): entry is CachedRepositoryHostDetails {
	if (typeof entry !== "object" || entry === null) {
		return false;
	}
	const candidate = entry as Record<string, unknown>;
	const { license, topics } = candidate;
	return (
		["RepositoryID", "html_url", "full_name", "updated_at"].every((key) => typeof candidate[key] === "string") &&
		typeof candidate.forks_count === "number" &&
		(candidate.description === null || typeof candidate.description === "string") &&
		(candidate.visibility === undefined || typeof candidate.visibility === "string") &&
		(topics === undefined || (Array.isArray(topics) && topics.every((topic) => typeof topic === "string"))) &&
		(license === null || typeof (license as { name?: unknown } | undefined)?.name === "string")
	);
}

/*
 * Maps an error thrown by sdk.repositories.update into the three
 * states the UI surfaces via RemoteAccessState. The SDK throws SdkError
 * for any non-2xx HTTP response; axios errors only leak through when the
 * SDK couldn't get a response at all (network down, DNS failure, etc).
 * Anything else (defensive) is the "unknown" branch.
 */
function applyRepoUpdateError(error: unknown, updateStatus: RemoteAccessState, updatedSetting: string) {
	// Only ever called from a catch, so every branch is a failure: flag the
	// error unconditionally and vary only the message by error class.
	updateStatus.error = true;
	if (error instanceof SdkError) {
		updateStatus.message =
			extractSdkErrorMessage(error) ||
			`An error occurred while updating the ${updatedSetting}. Please try again.`;
	} else if (axios.isAxiosError(error)) {
		updateStatus.message = `Failed to update the ${updatedSetting}. Make sure you have an active internet connection.`;
	} else {
		updateStatus.message = `Could not update the ${updatedSetting}. Try again.`;
	}
}

export const useRepositoriesStore = defineStore("repositories", {
	state: (): StoreState => ({
		connectedRepositories: undefined,
		remoteRepositoryUpdateStatus: { loading: false, error: false, message: "" },
		repositoryListServedFromCache: false,
		repositoryListFetchInFlight: undefined,
		confirmedRepositoryIDs: new Set(),
		pendingFieldServerValues: markRaw(new Map()),
		fieldWriteGenerations: markRaw(new WriteGenerations<string>()),
		pendingRepositoryWrites: markRaw(new Map()),
		fieldWriteConfirmations: markRaw(new Map()),
		disconnectConfirmations: markRaw(new Map()),
		repositoryHostDetails: {},
	}),

	actions: {
		async fetchRepositoryByID(repositoryID: string) {
			try {
				const confirmationsBeforeRequest = writeConfirmationCount;
				const response = await sdk.repositories.getOne(repositoryID);
				const repository = response as Repository;

				if (!this.isDisconnectedSince(repositoryID, confirmationsBeforeRequest)) {
					this.connectedRepositories = util.mergeByID(
						this.connectedRepositories ?? [],
						[this.keepLocalWrites(repository, confirmationsBeforeRequest)],
						(r) => r.RepositoryID,
					);
					this.confirmedRepositoryIDs.add(repositoryID);
				}

				return repository;
			} catch (error) {
				if (
					error instanceof SdkError &&
					error.code === ERROR_CODES.API_NOT_FOUND &&
					this.connectedRepositories
				) {
					this.connectedRepositories = this.connectedRepositories.filter(
						(r) => r.RepositoryID !== repositoryID,
					);
				}
				throw error;
			}
		},
		isDisconnectedSince(repositoryID: string, confirmationsBeforeRequest: number): boolean {
			return (this.disconnectConfirmations.get(repositoryID) ?? 0) > confirmationsBeforeRequest;
		},
		keepLocalWrites(fetchedRepository: Repository, confirmationsBeforeRequest: number): Repository {
			const localRepository = this.connectedRepositories?.find(
				(repo) => repo.RepositoryID === fetchedRepository.RepositoryID,
			);
			if (!localRepository) {
				return fetchedRepository;
			}
			const keptKeys = (Object.keys(localRepository) as (keyof Repository)[]).filter((key) => {
				const fieldKey = `${fetchedRepository.RepositoryID}:${key}`;
				const isPending = this.pendingFieldServerValues.has(fieldKey);
				const isConfirmedSinceRequest =
					(this.fieldWriteConfirmations.get(fieldKey) ?? 0) > confirmationsBeforeRequest;
				// A pending write that fails rolls back to the list's value.
				if (isPending && !isConfirmedSinceRequest) {
					this.pendingFieldServerValues.set(fieldKey, fetchedRepository[key]);
				}
				return isPending || isConfirmedSinceRequest;
			});
			return { ...fetchedRepository, ...pickKeys(localRepository, keptKeys) };
		},
		async getRepositoryByID(repositoryID: string) {
			if (this.connectedRepositories) {
				// if connectedRepositories is truthy, there is at least one repo in memory
				const repoInMemory = this.connectedRepositories.find((repoInMemory) => {
					return repoInMemory.RepositoryID === repositoryID;
				});
				if (repoInMemory) {
					return repoInMemory;
				}
			}
			// For now, always fetch from remote as a fallback
			const repository = await this.fetchRepositoryByID(repositoryID);
			return repository;
		},
		updateRepoInMemoryByID(repositoryID: string, repositoryItem: Repository) {
			if (this.connectedRepositories) {
				this.connectedRepositories = this.connectedRepositories.map((repoInMemory) =>
					repoInMemory.RepositoryID === repositoryID ? repositoryItem : repoInMemory,
				);
			}
		},
		async connectToRepository(repoRequestConfig: RepositoryConnectionRequestConfig) {
			return sdk.repositories.connect(repoRequestConfig);
		},
		async disconnectRepositoryLocalFirst(repositoryID: string): Promise<string | undefined> {
			const isThisRepository = (repository: Repository) => repository.RepositoryID === repositoryID;
			const originalIndex = this.connectedRepositories?.findIndex(isThisRepository) ?? -1;
			const disconnectedRepository = this.connectedRepositories?.[originalIndex];
			let failureMessage: string | undefined;

			// Infinity while in flight: every load, whenever requested, leaves the repository out.
			this.disconnectConfirmations.set(repositoryID, Infinity);
			this.connectedRepositories = this.connectedRepositories?.filter(
				(repository) => !isThisRepository(repository),
			);

			await saveOrRevert(
				async () => {
					try {
						await sdk.repositories.disconnect(repositoryID);
					} catch (error) {
						if (!(error instanceof SdkError && error.code === ERROR_CODES.API_NOT_FOUND)) {
							this.disconnectConfirmations.delete(repositoryID);
							monitoringCaptureError(error, "Disconnect repository");
							failureMessage = axios.isAxiosError(error)
								? "Failed to disconnect the repository. Make sure you have an active internet connection."
								: "Could not disconnect the repository. Try again.";
							return false;
						}
					}
					this.disconnectConfirmations.set(repositoryID, ++writeConfirmationCount);
					return true;
				},
				() => this.connectedRepositories?.some(isThisRepository) ?? false,
				() => {
					if (!disconnectedRepository) {
						return;
					}
					const currentRepositories = this.connectedRepositories ?? [];
					this.connectedRepositories = [
						...currentRepositories.slice(0, originalIndex),
						disconnectedRepository,
						...currentRepositories.slice(originalIndex),
					];
					this.fetchRepositoryByID(repositoryID).catch((error) => {
						if (!(error instanceof SdkError && error.code === ERROR_CODES.API_NOT_FOUND)) {
							monitoringCaptureError(error, "Refetch repository after failed disconnect");
						}
					});
				},
			);
			return failureMessage;
		},
		async updateRepositoryConfig(
			repositoryID: string,
			repoRequestConfig: Partial<RepositoryConnectionRequestConfig>,
		) {
			return sdk.repositories.update(repositoryID, repoRequestConfig);
		},
		async removeRepositoryConfigKey(repositoryID: string, keysToRemove: RepositoryPatchRemovableField[]) {
			return sdk.repositories.update(repositoryID, {}, { remove: keysToRemove });
		},
		// Paint the cached list immediately. The fetch replaces it once it lands.
		async hydrateRepositoryListFromCache(listAnswered: () => boolean = () => false): Promise<void> {
			const [painted] = await Promise.all([
				hydrateCachedList(
					REPOSITORY_LIST_CACHE_KEY,
					isCachedRepository,
					() => this.connectedRepositories,
					(repository) => repository.RepositoryID,
				),
				this.drawSavedRepositoryHostDetails(),
			]);
			if (painted && !listAnswered()) {
				this.connectedRepositories = painted;
				this.repositoryListServedFromCache = true;
				this.mirrorRepositoryListsToCache();
			}
		},
		async drawSavedRepositoryHostDetails(): Promise<void> {
			if (Object.keys(this.repositoryHostDetails).length > 0) {
				return;
			}
			const savedHostDetails = await readCachedList(
				REPOSITORY_HOST_DETAILS_CACHE_KEY,
				isCachedRepositoryHostDetails,
			);
			if (savedHostDetails) {
				const savedByID = Object.fromEntries(
					savedHostDetails.map(({ RepositoryID, ...details }) => [RepositoryID, details]),
				);
				this.repositoryHostDetails = { ...savedByID, ...this.repositoryHostDetails };
			}
		},
		forgetRepositoryHostDetails(repositoryID: string): void {
			delete this.repositoryHostDetails[repositoryID];
		},
		saveRepositoryHostDetails(repositoryID: string, details: RepositoryHostDetails): void {
			this.repositoryHostDetails[repositoryID] = {
				html_url: details.html_url,
				full_name: details.full_name,
				description: details.description,
				topics: details.topics,
				visibility: details.visibility,
				license: details.license && { name: details.license.name },
				updated_at: details.updated_at,
				forks_count: details.forks_count,
			};
		},
		mirrorRepositoryListsToCache(): void {
			mirrorStoreListToCache(this, REPOSITORY_LIST_CACHE_KEY, () => this.connectedRepositories);
			mirrorStoreListToCache(this, REPOSITORY_HOST_DETAILS_CACHE_KEY, () => this.savedRepositoryHostDetails);
		},
		async fetchRemoteRepositories() {
			this.repositoryListFetchInFlight ??= (async () => {
				//TODO: add pagination
				let listAnswered = false;
				this.remoteRepositoryUpdateStatus.loading = true;
				await Promise.all([
					this.hydrateRepositoryListFromCache(() => listAnswered),
					(async () => {
						try {
							const confirmationsBeforeRequest = writeConfirmationCount;
							const response = await sdk.repositories.list();
							listAnswered = true;
							// Not earlier, or the partial list fetchRepositoryByID builds would be cached.
							this.mirrorRepositoryListsToCache();
							// The list is older than a write still in flight or confirmed since it was requested.
							this.connectedRepositories = (response.Repositories as Repository[])
								.filter(
									(listedRepository) =>
										!this.isDisconnectedSince(
											listedRepository.RepositoryID,
											confirmationsBeforeRequest,
										),
								)
								.map((listedRepository) =>
									this.keepLocalWrites(listedRepository, confirmationsBeforeRequest),
								);
							this.repositoryListServedFromCache = false;
							for (const { RepositoryID } of response.Repositories as Repository[]) {
								this.confirmedRepositoryIDs.add(RepositoryID);
							}
							this.remoteRepositoryUpdateStatus.error = false;
						} catch (error) {
							this.remoteRepositoryUpdateStatus.error = true;
							monitoringCaptureError(error, "Get user repository list");
						} finally {
							this.remoteRepositoryUpdateStatus.loading = false;
						}
					})(),
				]);
			})().finally(() => {
				this.repositoryListFetchInFlight = undefined;
			});
			return this.repositoryListFetchInFlight;
		},

		/*
		 * Repository update functions
		 */

		/*
		 *	Local-first: the edited field lands in the store now, the PATCH runs in the
		 *	background. The response reconciles the written fields (the server may
		 *	normalize them). A failure restores them and reports through `updateStatus`.
		 */
		updateRepositoryLocalFirst(
			repositoryID: string,
			updatedFields: Partial<RepositoryConnectionRequestConfig>,
			updateStatus: RemoteAccessState,
			monitoringContext: string,
			updatedSetting: string,
		): Promise<RepositoryWriteOutcome> {
			const updatedValues = updatedFields as Partial<Repository>;
			const updatedKeys = Object.keys(updatedFields) as (keyof Repository)[];
			const fieldKey = (key: string) => `${repositoryID}:${key}`;
			const claim = this.fieldWriteGenerations.claim(updatedKeys.map(fieldKey));
			// Fields a newer write has claimed since are its to reconcile or roll back, not ours.
			const ownedKeys = () => updatedKeys.filter((key) => this.fieldWriteGenerations.owns(fieldKey(key), claim));
			const releaseFields = (keys: (keyof Repository)[]) => {
				for (const key of keys) {
					this.pendingFieldServerValues.delete(fieldKey(key));
				}
			};
			const findRepository = () => this.connectedRepositories?.find((repo) => repo.RepositoryID === repositoryID);
			// Look the repository up on every write: the list may have been replaced
			// by a refetch while the request was in flight.
			const applyFields = (fields: Partial<Repository>) => {
				if (this.connectedRepositories) {
					this.connectedRepositories = this.connectedRepositories.map((repo) =>
						repo.RepositoryID === repositoryID ? { ...repo, ...fields } : repo,
					);
				}
			};
			const repositoryBeforeUpdate = findRepository();

			for (const key of updatedKeys) {
				// The value on screen may be an older write that has not reached the server yet.
				if (!this.pendingFieldServerValues.has(fieldKey(key))) {
					this.pendingFieldServerValues.set(fieldKey(key), repositoryBeforeUpdate?.[key]);
				}
			}
			applyFields(updatedFields);
			// A previous failure must not outlive the retry that replaces it.
			updateStatus.error = false;
			updateStatus.message = "";

			const write = this.updateRepositoryConfig(repositoryID, updatedFields)
				.then((response): RepositoryWriteOutcome => {
					const keysToReconcile = ownedKeys();
					// Only keys the response actually carries: an omitted one must not blank the local value.
					const remoteFields = (response ?? {}) as Partial<Repository>;
					const echoedKeys = keysToReconcile.filter((key) => key in remoteFields);
					applyFields(pickKeys(remoteFields, echoedKeys));
					for (const key of updatedKeys) {
						this.fieldWriteConfirmations.set(fieldKey(key), ++writeConfirmationCount);
						const hasNewerWrite =
							this.pendingFieldServerValues.has(fieldKey(key)) &&
							!this.fieldWriteGenerations.owns(fieldKey(key), claim);
						if (hasNewerWrite) {
							this.pendingFieldServerValues.set(
								fieldKey(key),
								key in remoteFields ? remoteFields[key] : updatedValues[key],
							);
						}
					}
					releaseFields(keysToReconcile);
					return keysToReconcile.length > 0 ? "saved" : "superseded";
				})
				.catch((error): RepositoryWriteOutcome => {
					monitoringCaptureError(error, monitoringContext);
					const keysToRollBack = ownedKeys();
					if (keysToRollBack.length === 0) {
						return "superseded";
					}
					if (repositoryBeforeUpdate) {
						const repository = findRepository();
						// A refetch that replaced the value wins over the rollback.
						const unchangedKeys = keysToRollBack.filter(
							(key) => toRaw(repository?.[key]) === updatedValues[key],
						);
						applyFields(
							Object.fromEntries(
								unchangedKeys.map((key) => [key, this.pendingFieldServerValues.get(fieldKey(key))]),
							),
						);
					}
					releaseFields(keysToRollBack);
					applyRepoUpdateError(error, updateStatus, updatedSetting);
					return "failed";
				});

			const repositoryWrites = this.pendingRepositoryWrites.get(repositoryID) ?? new Set();
			this.pendingRepositoryWrites.set(repositoryID, repositoryWrites);
			repositoryWrites.add(write);
			void write.then(() => {
				repositoryWrites.delete(write);
				if (repositoryWrites.size === 0) {
					this.pendingRepositoryWrites.delete(repositoryID);
				}
			});
			return write;
		},
		async waitForRepositoryWrites(repositoryID: string): Promise<boolean> {
			const outcomes = await Promise.all(this.pendingRepositoryWrites.get(repositoryID) ?? []);
			return !outcomes.includes("failed");
		},
		async setRepositoryCoreID(repositoryID: string, coreID: string, updateStatus: RemoteAccessState) {
			return this.updateRepositoryLocalFirst(
				repositoryID,
				{ Core: coreID },
				updateStatus,
				"Set repository core",
				"core",
			);
		},
		async setRepositoryWorkingBranch(
			repositoryID: string,
			branchName: string,
			commitHash: string,
			updateStatus: RemoteAccessState,
		) {
			return this.updateRepositoryLocalFirst(
				repositoryID,
				{ Branch: branchName, Commit: commitHash },
				updateStatus,
				"Set repository working directory",
				"working branch",
			);
		},
		async setRepositoryRunArguments(repositoryID: string, runArguments: string, updateStatus: RemoteAccessState) {
			return this.updateRepositoryLocalFirst(
				repositoryID,
				{ Arguments: runArguments },
				updateStatus,
				"Set repository run arguments",
				"runtime arguments",
			);
		},
		async setRepositoryBuildDirectory(
			repositoryID: string,
			buildDirectory: string,
			updateStatus: RemoteAccessState,
		) {
			return this.updateRepositoryLocalFirst(
				repositoryID,
				{ BuildDirectory: buildDirectory },
				updateStatus,
				"Set repository build directory",
				"build directory",
			);
		},
		async setRepositoryDataSources(
			repositoryID: string,
			mountConfig: null | undefined | LocalMountConfig,
			updateStatus: RemoteAccessState,
		): Promise<RepositoryWriteOutcome> {
			let partialRepoConfigToUpdate: Partial<RepositoryConnectionRequestConfig>;

			// Resolving the payload can itself hit the network (drive lookup), so it
			// reports through the same contract as the update it feeds.
			updateStatus.loading = true;
			try {
				if (mountConfig === null) {
					// if mountConfig = null it means we want to disable mounting
					partialRepoConfigToUpdate = {
						DataSources: [],
					};
				} else {
					// if mountConfig is not valid or the user requested to reset to default
					if (!isLocalMountConfig(mountConfig) || mountConfig === undefined) {
						// if invalid mount config try to set to the default configs
						const dataSourcesStore = useDataSourcesStore();
						mountConfig =
							(await dataSourcesStore.getGlobalDefaultMountConfig()) ??
							(await dataSourcesStore.getFallbackMountConfig());
					}

					// construct the partial repo config object
					partialRepoConfigToUpdate = {
						DataSources: await dsUtil.formatDataSourcesPayload(
							mountConfig?.ResourceID,
							mountConfig?.ResourceType,
							mountConfig?.Location,
						),
					};
				}
			} catch (error) {
				monitoringCaptureError(error, "Set repository data sources");
				applyRepoUpdateError(error, updateStatus, "data sources");
				return "failed";
			} finally {
				updateStatus.loading = false;
			}

			return this.updateRepositoryLocalFirst(
				repositoryID,
				partialRepoConfigToUpdate,
				updateStatus,
				"Set repository data sources",
				"data sources",
			);
		},
	},
	getters: {
		getConnectedRepositories: (state) => {
			if (!state.connectedRepositories) {
				return [] as Repository[];
			}
			return state.connectedRepositories;
		},
		isURLConnected:
			(state) =>
			(remoteURL: string): boolean => {
				return state.connectedRepositories?.some((repo) => repo.RemoteURL === remoteURL) ?? false;
			},
		savedRepositoryHostDetails: (state): CachedRepositoryHostDetails[] | undefined =>
			state.connectedRepositories?.flatMap(({ RepositoryID }) => {
				const details = state.repositoryHostDetails[RepositoryID];
				return details ? [{ RepositoryID, ...details }] : [];
			}),
		isConfirmed:
			(state) =>
			(repositoryID: string): boolean =>
				state.confirmedRepositoryIDs.has(repositoryID),
		loadError: (state): boolean => state.remoteRepositoryUpdateStatus.error,
		// Stale once a refresh failed, and still stale while the retry runs.
		repositoryListIsStale: (state): boolean =>
			state.repositoryListServedFromCache && state.remoteRepositoryUpdateStatus.error,
		repositoryListNeedsFetch: (state): boolean =>
			state.connectedRepositories === undefined || state.repositoryListServedFromCache,
	},
});
